/**
 * Promotions (IhmPromotionsPlatformService) CrossFrame Listeners
 *
 * Handles the PlatformClient.promotions calls (JWOStoreManagementPlatformComponents,
 * src/client/interface/PromotionsInterface.ts). Validates the required parameters and delegates to the
 * gateway client via getServiceClient(); the Gateway verifies merchantId against the caller's scope
 * and forwards it to the backend as the MONS merchant.
 *
 * merchantId is checked here although the backend model marks it optional, because the Gateway
 * rejects a call without it; failing early saves a round trip.
 */

import { getServiceClient } from '@api/client-factory/StoreManagementClientFactory'

import {
    CancelPromotionRequest,
    CancelPromotionResponse,
    CreatePromotionRequest,
    CreatePromotionResponse,
    GetPromotionsRequest,
    GetPromotionsResponse,
    GetSkuListingsRequest,
    GetSkuListingsResponse,
} from '@amzn/coral_com-amazon-ihm-promotionsplatformservice'
import { ValidationException } from '@amzn/coral_com-amazon-jwostoremanagementgateway'
import { validateRequired, validateRequiredArray } from './listenerUtils'

/**
 * Tools send dates as ISO-8601 strings (or epoch millis), but the Coral serializer calls
 * Date#getTime(), so a string would throw at request time. Tools are likely to send strings: the
 * shell JSON-serializes every response (CrossFrameUtils.handlePostMessageRequestEvent), so the
 * Promotion dates a tool receives are strings. A Date passes through unchanged (postMessage's
 * structured clone keeps Date objects).
 *
 * An unparseable value is rejected here rather than sent as NaN. It is thrown as the Gateway's
 * ValidationException so the tool sees the same `__type` it would get had the Gateway rejected the
 * input; a plain Error would reach the tool as an untyped failure.
 */
export function toDate (value: unknown, field: string): Date | undefined {
    if (value === undefined || value === null || value === '') return undefined
    const date = value instanceof Date ? value : new Date(value as string | number)
    if (Number.isNaN(date.getTime())) {
        throw ValidationException.clone({ message: `${field} is not a valid date` })
    }
    return date
}

/**
 * One page of the merchant's promotions.
 * Required: merchantId, marketplaceId, locale
 * searchPromotionFilter.startDate/endDate are converted with toDate, like createPromotion's dates.
 */
export async function getPromotionsListener (message: any): Promise<GetPromotionsResponse> {
    const params = (message && message.data) || {}
    validateRequired(params, 'merchantId', 'marketplaceId', 'locale')
    const filter = params.searchPromotionFilter
    const client = getServiceClient()
    return client.getPromotions(GetPromotionsRequest.clone({
        ...params,
        ...(filter && {
            searchPromotionFilter: {
                ...filter,
                startDate: toDate(filter.startDate, 'searchPromotionFilter.startDate'),
                endDate: toDate(filter.endDate, 'searchPromotionFilter.endDate'),
            },
        }),
    })).toPromise()
}

/**
 * Create a promotion, or save/publish a draft.
 * Required: merchantId, marketplaceId, locale, action, name, createdBy (any placeholder; the Gateway
 * overwrites it with the caller).
 */
export async function createPromotionListener (message: any): Promise<CreatePromotionResponse> {
    const params = (message && message.data) || {}
    validateRequired(params, 'merchantId', 'marketplaceId', 'locale', 'action', 'name', 'createdBy')
    const client = getServiceClient()
    return client.createPromotion(CreatePromotionRequest.clone({
        ...params,
        startDate: toDate(params.startDate, 'startDate'),
        endDate: toDate(params.endDate, 'endDate'),
    })).toPromise()
}

/**
 * Cancel a promotion, or delete a draft.
 * Required: merchantId, marketplaceId, id
 */
export async function cancelPromotionListener (message: any): Promise<CancelPromotionResponse> {
    const params = (message && message.data) || {}
    validateRequired(params, 'merchantId', 'marketplaceId', 'id')
    const client = getServiceClient()
    return client.cancelPromotion(CancelPromotionRequest.clone(params)).toPromise()
}

/**
 * Look up SKUs the merchant lists.
 * Required: merchantId, marketplaceId, itemIdList (non-empty)
 */
export async function getSkuListingsListener (message: any): Promise<GetSkuListingsResponse> {
    const params = (message && message.data) || {}
    validateRequired(params, 'merchantId', 'marketplaceId')
    validateRequiredArray(params, 'itemIdList')
    const client = getServiceClient()
    return client.getSkuListings(GetSkuListingsRequest.clone(params)).toPromise()
}
